import React from 'react';
import { Chip } from '@mui/material';
import PropTypes from 'prop-types';

/**
 * StatusChip Component
 *
 * A standardized status chip component with fixed dimensions.
 * All status chips use the same size regardless of text length.
 *
 * @param {Object} props - Component props
 * @param {string} props.label - Chip label text
 * @param {string} props.color - MUI color name (default, primary, secondary, error, info, success, warning)
 * @param {string} props.status - Status value (for draft special styling)
 * @param {Object} props.sx - Additional sx styles to merge
 * @param {string} props.backgroundColor - Custom background color
 * @param {string} props.textColor - Custom text color
 */
const StatusChip = ({
  label,
  color = 'default',
  status,
  sx = {},
  backgroundColor,
  textColor,
  ...otherProps
}) => {
  const isDraft = status?.toLowerCase() === 'draft';

  return (
    <Chip
      label={label}
      color={color}
      size="small"
      sx={{
        width: 90,
        height: 24,
        fontSize: '0.75rem',
        fontWeight: 500,
        display: 'flex',
        justifyContent: 'center',
        '& .MuiChip-label': {
          px: 1.5,
          py: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          textAlign: 'center',
        },
        // Draft status special styling
        ...(isDraft
          ? {
              backgroundColor: '#ffc107', // Yellow color for draft
              color: '#000000', // Black text for contrast
              '&:hover': {
                backgroundColor: '#ffb300', // Slightly darker yellow on hover
              },
            }
          : {}),
        // Custom colors override
        ...(backgroundColor ? { backgroundColor } : {}),
        ...(textColor ? { color: textColor } : {}),
        // Additional custom styles
        ...sx,
      }}
      {...otherProps}
    />
  );
};

StatusChip.propTypes = {
  label: PropTypes.string.isRequired,
  color: PropTypes.oneOf([
    'default',
    'primary',
    'secondary',
    'error',
    'info',
    'success',
    'warning',
  ]),
  status: PropTypes.string,
  sx: PropTypes.object,
  backgroundColor: PropTypes.string,
  textColor: PropTypes.string,
};

export default StatusChip;
